import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import BookmarkAddedRoundedIcon from '@mui/icons-material/BookmarkAddedRounded';
import BusinessIcon from '@mui/icons-material/Business';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import EmojiObjectsRoundedIcon from '@mui/icons-material/EmojiObjectsRounded';
import GavelIcon from '@mui/icons-material/Gavel';
import GridViewIcon from '@mui/icons-material/GridView';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import HandymanIcon from '@mui/icons-material/Handyman';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import HomeIcon from '@mui/icons-material/Home';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PaidIcon from '@mui/icons-material/Paid';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import PinDropRoundedIcon from '@mui/icons-material/PinDropRounded';
import PsychologyAltRoundedIcon from '@mui/icons-material/PsychologyAltRounded';
import PublicIcon from '@mui/icons-material/Public';
import RadarIcon from '@mui/icons-material/Radar';
import SecurityIcon from '@mui/icons-material/Security';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ViewStreamRoundedIcon from '@mui/icons-material/ViewStreamRounded';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WindowIcon from '@mui/icons-material/Window';

/**
 * Unified icon mapping for report sections
 * Supports both regular and rounded icon variants
 * @param {string} sectionId - Section identifier
 * @param {string} [iconVariant='regular'] - Icon variant: 'regular' or 'rounded'
 * @returns {React.ReactElement|null} Icon component or null if section should have no icon
 */
export function getIconForSection(sectionId, iconVariant = 'regular') {
  const iconMap = {
    regular: {
      // Business Ideas sections
      overview: <GridViewIcon />,
      targetCustomers: <PeopleIcon />,
      revenueModel: <CurrencyExchangeIcon />,
      startupCosts: <AttachMoneyIcon />,
      marketingStrategy: <MyLocationIcon />,
      risks: <WarningAmberIcon />,
      growthOpportunities: <TrendingUpIcon />,
      whyThisWorksForYou: null,
      businessOverview: <GridViewIcon />,
      startupCostsInvestment: <AttachMoneyIcon />,
      risksChallenges: <WarningAmberIcon />,
      // Business Cost sections
      annualReport: <AssignmentIcon />,
      bookkeepingTools: <AccountCircleIcon />,
      formation: <WindowIcon />,
      generalOperatingCosts: <PaidIcon />,
      licensesPermits: <AssignmentIndIcon />,
      officeOrPhysicalLocation: <HomeIcon />,
      operatingEquipment: <HandymanIcon />,
      registeredAgent: <SecurityIcon />,
      summary: null,
      taxes: <CurrencyExchangeIcon />,
      estimatedTotal: <ViewStreamIcon />,
      websiteMarketingExpenses: <PublicIcon />,
      // Business Name sections
      similar_businesses: <BusinessIcon />,
      domains: <LanguageIcon />,
      trademark: <GavelIcon />,
      // Market Analysis sections
      executiveSummary: <AssignmentIcon />,
      marketOverview: <GridViewIcon />,
      marketGrowthTrend: <TrendingUpIcon />,
      marketTrends: <RadarIcon />,
      customerSegmentation: <PeopleIcon />,
      competitiveLandscape: <BusinessIcon />,
      competitorProfiles: <BusinessIcon />,
      swotAnalysis: <WarningAmberIcon />,
      actionableRecommendations: <TrendingUpIcon />,
      roiImpactLayer: <AttachMoneyIcon />,
      implementationGuidance: <MyLocationIcon />,
      suggestedNextSteps: <TrendingUpIcon />,
      methodology: <AssignmentIcon />,
      // Old format (snake_case)
      why_this_works_for_you: null,
      business_overview: <GridViewIcon />,
      target_customers: <PeopleIcon />,
      revenue_model: <CurrencyExchangeIcon />,
      startup_costs_and_investment: <AttachMoneyIcon />,
      'startup_costs_&_investment': <AttachMoneyIcon />,
      marketing_strategy: <RadarIcon />,
      risks_and_challenges: <WarningAmberIcon />,
      'risks_&_challenges': <WarningAmberIcon />,
      growth_opportunities: <TrendingUpIcon />,
      default: <GridViewIcon />
    },
    rounded: {
      // Business Cost sections (rounded icons)
      annualReport: <AssignmentRoundedIcon />,
      bookkeepingTools: <BookmarkAddedRoundedIcon />,
      default: <GridViewRoundedIcon />,
      estimatedTotal: <ViewStreamRoundedIcon />,
      formation: <GridViewRoundedIcon />,
      generalOperatingCosts: <PaymentsRoundedIcon />,
      licensesPermits: <BadgeRoundedIcon />,
      officeOrPhysicalLocation: <HomeWorkRoundedIcon />,
      operatingEquipment: <ConstructionRoundedIcon />,
      registeredAgent: <PersonAddAltRoundedIcon />,
      summary: null,
      taxes: <AttachMoneyRoundedIcon />,
      websiteMarketingExpenses: <CampaignRoundedIcon />,
      // Business Entity Advisor sections
      understandingConfidenceLevel: <InfoOutlinedIcon />,
      whyThisConfidenceLevel: <HelpOutlineRoundedIcon />,
      whyWeRecommendThis: <PsychologyAltRoundedIcon />,
      comparison: null,
      taxImplications: <InfoOutlinedIcon />,
      taxComparison: <PaymentsRoundedIcon />,
      stateTaxation: <PinDropRoundedIcon />,
      tips: <EmojiObjectsRoundedIcon />,
      stateFormationRequirements: <PinDropRoundedIcon />
    }
  };

  const variantMap = iconMap[iconVariant] || iconMap.regular;
  const icon = variantMap[sectionId];

  // Return null explicitly if mapped to null, otherwise return icon or default
  if (variantMap[sectionId] === null) {
    return null;
  }

  return icon || variantMap.default;
}

/**
 * Formats section titles from camelCase or snake_case keys
 * Converts technical keys to human-readable titles
 * @param {string} key - Section key (camelCase or snake_case)
 * @returns {string} Formatted title
 */
export function formatSectionTitle(key) {
  return key
    .replace(/_/g, ' ') // Replace underscores with spaces (for snake_case)
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters (for camelCase)
    .replace(/&/g, 'and') // Replace & with 'and'
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
}
